<template>
    <div class="sc-chat">
        <button
            v-show="!$isMobile"
            type="button"
            :class="{ opened: isOpen }"
            class="sc-launcher"
            :aria-label="isOpen ? 'Close chat' : 'Open chat'"
            data-test="chat-launcher"
            @click.prevent="isOpen ? close() : open()"
        >
            <div v-if="newMessagesCount > 0 && !isOpen" class="is-new-message-count sc-new-messsages-count">
                {{ newMessagesCount }}
            </div>

            <span class="sc-closed-icon">
                <e-icon icon="pi pi-comment" />
            </span>

            <span class="sc-open-icon">
                <e-icon icon="pi pi-times" />
            </span>
        </button>
        <ChatWindow
            :message-list="messageList"
            :messages-loading="messagesLoading"
            :message-history-count="messageHistoryCount"
            :new-messages-count="newMessagesCount"
            :current-conversation="currentConversation"
            :conversation-list="conversationList"
            :on-user-input-submit="onMessageWasSent"
            :on-conversation-switch="onConversationSwitch"
            :participants="participants"
            :participants-loading="participantsLoading"
            :title="title"
            :title-image-url="titleImageUrl"
            :is-open="isOpen"
            :on-close="close"
            :show-emoji="showEmoji"
            :show-file="showFile"
            :placeholder="placeholder"
            :show-typing-indicator="showTypingIndicator"
            :colors="colors"
            :always-scroll-to-bottom="alwaysScrollToBottom"
            :message-styling="messageStyling"
            :online="online"
            :requested-conversation="requestedConversation"
            @typing="$emit('typing')"
            @load-message-history="$emit('load-message-history')"
            @switch-window="window => $emit('switch-window', window)"
        />
    </div>
</template>
<script>
import ChatWindow from './ChatWindow.vue';

export default {
    name: 'ChatWidget',

    components: { ChatWindow },

    emits: ['typing', 'load-message-history', 'switch-window'],

    props: {
        showEmoji: {
            type: Boolean,
            default: false,
        },
        isOpen: {
            type: Boolean,
            required: true,
        },
        open: {
            type: Function,
            required: true,
        },
        close: {
            type: Function,
            required: true,
        },
        showFile: {
            type: Boolean,
            default: false,
        },
        participants: {
            type: Array,
            required: true,
        },
        participantsLoading: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            default: () => '',
        },
        titleImageUrl: {
            type: String,
            default: () => '',
        },
        onMessageWasSent: {
            type: Function,
            required: true,
        },
        onConversationSwitch: {
            type: Function,
            required: true,
        },
        messageList: {
            type: Array,
            default: () => [],
        },
        messagesLoading: {
            type: Boolean,
            default: false,
        },
        messageHistoryCount: {
            type: Number,
            default: 0,
        },
        currentConversation: {
            type: Number,
            required: true,
        },
        conversationList: {
            type: Array,
            default: () => [],
        },
        newMessagesCount: {
            type: Number,
            default: () => 0,
        },
        placeholder: {
            type: String,
            default: 'Write a reply',
        },
        showTypingIndicator: {
            type: Number,
            default: null,
        },
        online: {
            type: Object,
            default: () => {},
        },
        requestedConversation: {
            type: Object,
            default: null,
        },
        colors: {
            type: Object,
            required: false,
            validator: c =>
                'header' in c &&
                'bg' in c.header &&
                'text' in c.header &&
                'progress' in c &&
                'bg' in c.progress &&
                'fg' in c.progress &&
                'launcher' in c &&
                'bg' in c.launcher &&
                'messageList' in c &&
                'bg' in c.messageList &&
                'conversationList' in c &&
                'bg' in c.conversationList &&
                'sentMessage' in c &&
                'bg' in c.sentMessage &&
                'text' in c.sentMessage &&
                'receivedMessage' in c &&
                'bg' in c.receivedMessage &&
                'text' in c.receivedMessage &&
                'userInput' in c &&
                'bg' in c.userInput &&
                'text' in c.userInput,
            default() {
                return {
                    header: {
                        bg: '#335CE5',
                        text: '#FFFFFF',
                    },
                    progress: {
                        bg: '#242B36',
                        fg: '#555',
                    },
                    launcher: {
                        bg: '#335CE5',
                    },
                    messageList: {
                        bg: '#FFFFFF',
                    },
                    conversationList: {
                        bg: '#FFFFFF',
                    },
                    sentMessage: {
                        bg: '#335CE5',
                        text: '#FFFFFF',
                    },
                    receivedMessage: {
                        bg: '#E5EFF5',
                        text: '#FFFFFF',
                    },
                    userInput: {
                        bg: '#E5EFF5',
                        text: '#565867',
                    },
                };
            },
        },
        alwaysScrollToBottom: {
            type: Boolean,
            default: () => false,
        },
        messageStyling: {
            type: Boolean,
            default: () => false,
        },
        authUser: {
            type: Object,
            default: () => null,
        },
    },

    created() {
        this.$store.dispatch('Chat/setAuthUser', this.authUser);
    },
};
</script>

<style lang="scss" scoped>
@import 'resources/scss/toolbox.scss';

// Above .layout-topbar (z-index 1000) so the chat header (back/close) is not covered by
// the fixed top navigation: mobile fullscreen chat starts at top 0, and the desktop window
// reaches under the 5rem topbar on viewports shorter than ~774px (e.g. 1366x768 laptops).
.sc-chat {
    position: relative;
    z-index: 1100;
}

.sc-launcher {
    background-color: var(--primary-color) !important;
    width: $size-1;
    height: $size-1;
    position: fixed;
    right: 1.125rem;
    // Lift above a page action bar (StickyActionBar) while one is mounted; unset elsewhere.
    bottom: calc(3.5rem + var(--page-action-bar-height, 0px));
    border-radius: 50%;
    box-shadow: none;
    transition: $transition-medium;
    cursor: pointer;
}

.sc-launcher:before {
    content: '';
    position: relative;
    display: block;
    width: $size-1;
    height: $size-1;
    border-radius: 50%;
    transition: $transition-medium;
}

.sc-launcher .sc-open-icon,
.sc-launcher .sc-closed-icon {
    color: $primary-invert;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition:
        opacity 100ms ease-in-out,
        transform 100ms ease-in-out;
}

.sc-launcher .sc-closed-icon {
    transition:
        opacity 100ms ease-in-out,
        transform 100ms ease-in-out;
}

.sc-launcher .sc-open-icon {
    box-sizing: border-box;
    opacity: 0;
}

.sc-launcher.opened .sc-open-icon {
    transform: rotate(-90deg);
    opacity: 1;
}

.sc-launcher.opened .sc-closed-icon {
    transform: rotate(-90deg);
    opacity: 0;
}

.sc-launcher.opened:before,
.sc-launcher:hover {
    box-shadow: $card-shadow;
}

.sc-new-messsages-count {
    position: absolute;
    top: -5px;
    left: 75%;
}
</style>
